import type { MouseEvent } from "react";
import React, { memo, useCallback } from "react";
import type { CarConfigConfigureSection } from "../../../../../../utils/types/SectionTypes";
import Text from "../../../../../../../shared-components/css-modules/toyota/atoms/Text";
import Button from "../../../../../../../shared-components/css-modules/toyota/atoms/Button";
import styles from "./Overview.module.scss";
import { trackSummaryCardNav } from "../../../../../../utils/tracking";
import a11y from "../../../../../../../shared-components/css-modules/toyota/utils/Accessibility.module.scss";
import { CarConfigLabel, getCarConfigLabel } from "../../../../../../utils/labelUtils";
import type { CarConfigLabelType } from "../../../../../../utils/labels";
import { scrollToTarget } from "../../../../../../../../../common-deprecated/utils/scrollUtils";
import type { CarConfigDispatchType } from "../../../../../../redux/store";
import { useCarConfigSelector } from "../../../../../../redux/store";
import { setActiveSection } from "../../../../../../redux/slices/CarConfigNavigationSlice";
import Icon from "../../../../../../../shared-components/css-modules/toyota/atoms/Icon";
import { useDispatch } from "../../../../../../../../../shared-logic/redux/store";

type OverviewItemWrapperProps = {
    title: CarConfigLabelType;
    editSection: CarConfigConfigureSection;
    children: React.ReactNode;
    image?: string;
    bg?: string;
};

const OverviewItemWrapper: React.FC<OverviewItemWrapperProps> = (props) => {
    const { title, editSection, children, image, bg } = props;
    const dispatch = useDispatch<CarConfigDispatchType>();
    const commonSettings = useCarConfigSelector((state) => state.commonSettings);
    const carConfigSettings = useCarConfigSelector((state) => state.carConfigSettings);

    const handleClick = useCallback(
        (event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
            if (editSection) {
                event.preventDefault();
                trackSummaryCardNav(editSection);
                scrollToTarget(editSection);

                const verticalScroll = window.matchMedia("(min-width: 1200px)");
                if (!verticalScroll.matches) {
                    dispatch(setActiveSection(editSection));
                }
            }
        },
        [editSection, dispatch],
    );

    if (!image || !title || !children) return null;

    return (
        <li className={styles.listitem}>
            {image && (
                <div className={styles.image} style={{ background: bg }}>
                    <img src={image} alt="" role="presentation" />
                </div>
            )}
            <div>
                <header className={styles.header}>
                    <Text tag="h3" variant="apheleia-body" className={styles.title}>
                        <CarConfigLabel label={title} />
                    </Text>
                    <Button variant="btn-icon-text" onClick={handleClick}>
                        <Icon variant="pencil" sizing="sm" aria-hidden="true" />
                        {getCarConfigLabel({ commonSettings, carConfigSettings }, "carConfigOverviewEdit")}
                        <span className={a11y["sr-only"]}>
                            {getCarConfigLabel({ commonSettings, carConfigSettings }, title)}
                        </span>
                    </Button>
                </header>
                {children}
            </div>
        </li>
    );
};

export default memo(OverviewItemWrapper);
